import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Coins } from "lucide-react";

export default function Account() {
  const nav = useNavigate();
  const { user, authLoaded, t } = useApp();
  const [orders, setOrders] = useState([]);
  const [loyalty, setLoyalty] = useState(null);

  useEffect(() => {
    if (!authLoaded) return;
    if (!user) { nav("/login"); return; }
    api.get("/orders/mine").then((r) => setOrders(r.data));
    api.get("/loyalty").then((r) => setLoyalty(r.data));
  }, [user, authLoaded, nav]);

  if (!user) return null;

  return (
    <div className="mx-auto max-w-5xl px-4 py-12">
      <h1 className="text-4xl font-display font-bold mb-2">Olá, {user.name}</h1>
      <p className="text-muted-foreground mb-8">A tua conta VOLTIX</p>

      <div className="grid md:grid-cols-4 gap-4 mb-8">
        <Card className="p-5 col-span-2 bg-gradient-to-br from-primary/10 to-accent/10 border-primary/20" data-testid="loyalty-card">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-primary"><Coins className="w-4 h-4" /> Pontos VOLTIX</div>
          <div className="font-display font-bold text-4xl mt-2">{loyalty?.points ?? 0}</div>
          <div className="text-sm text-muted-foreground mt-1">Valem <span className="font-semibold text-foreground">€{loyalty?.value_eur.toFixed(2) ?? "0.00"}</span> em descontos</div>
          <div className="text-xs text-muted-foreground mt-3">{loyalty?.rule}</div>
        </Card>
        <Card className="p-5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Email</div>
          <div className="font-medium mt-1 text-sm truncate">{user.email}</div>
        </Card>
        <Card className="p-5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Encomendas</div>
          <div className="font-medium mt-1">{orders.length}</div>
        </Card>
      </div>

      <h2 className="text-2xl font-display font-semibold mb-4">Histórico de encomendas</h2>
      {orders.length === 0 ? (
        <div className="text-muted-foreground text-sm">Ainda não tens encomendas.</div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => {
            const second = o.installments?.find((i) => i.number === 2);
            const pendingSecond = second && second.status === "unpaid";
            return (
              <Link to={`/encomenda/${o.number}`} key={o.id} className="block rounded-2xl border border-border p-5 bg-card hover:border-primary/40 transition-colors" data-testid={`order-${o.number}`}>
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <div className="font-mono font-semibold">{o.number}</div>
                    <div className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleString("pt-PT")}</div>
                    {o.pickup_code && <div className="font-mono text-xs text-primary font-bold mt-1 tracking-widest">🎫 {o.pickup_code}</div>}
                  </div>
                  <div className="flex items-center gap-3">
                    {pendingSecond && <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-500/15 text-yellow-700">2ª parcela pendente</span>}
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${o.payment_status === "paid" ? "bg-green-500/15 text-green-600" : "bg-muted"}`}>{t(`status.${o.payment_status}`)}</span>
                    <span className="text-xs px-3 py-1 rounded-full bg-muted">{t(`status.${o.status}`)}</span>
                    <div className="font-display font-bold">{o.pricing.total.toFixed(2)} {o.currency}</div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
